import React, { useCallback, useMemo, useState } from 'react'
import { styled } from '~/app/components'
import { DateTime } from 'luxon'
import 'moment/locale/ko'
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '~/app/components/common/Button/Button'
import { isAvailableDomesticAccommodationOption } from '~/app/components/Cart/CartOrderHelper'
import {
  BadgeContainer,
  DescContainer,
  ItemContainer,
  OptionDescriptionContainer,
  OptionNameContainer,
  PriceContainer,
  WarningNotice,
} from '~/app/components/Cart/CartItem/CartItem.styles'
import yaLogo from '~/app/imgs/empty-thumnail-yanolja-h-56.svg?inline'
import { Icon_close, Icon_info } from '@yanolja-rnd/ya-com-system-icon'
import {
  CancelPolicySummary,
  DomesticAccommodationUseType,
  PropertyCategory,
} from '~/__generated__/__types__'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { ConfigBadgeWrapper, FillRectBadge } from '~/app/components/common/Badge/Badge.styles'
import ConfigBadgeGroup from '~/app/components/common/Badge/ConfigBadgeGroup'
import {
  badgeWithPolicyInCartOption,
  getHasDayUseBadge,
} from '~/api/utils/accommodationBadgesWithPolicy'
import ExceptionalCancelPolicyText from '~/app/components/common/ExceptionalCancelPolicy/ExceptionalCancelPolicyText'
import { toKRW } from '~/app/utils/krNumberToString'
import { CartItemsV3Query } from '~/__generated__/app/query/Cart/Cart.graphql'
import getSortCartPropertyCoupons from '~/app/utils/getSortCartPropertyCoupons'
import { getDomesticAccommodationCancelSummary } from '~/app/utils/cancelPolicy'
import CancelPolicyText, {
  CancelPolicyFetchFailText,
} from '~/app/components/common/CancelPolicy/CancelPolicyText'
import { Typography } from '@yanolja-rnd/ya-com-core'
import { getPreOrderRatePlanOptions } from '~/app/utils/domesticRateplan'
import VipDiscountBadge from '~/app/components/common/Badge/VipDiscountBadge'
import CartVipBenefitNotice from '~/app/components/Cart/VipBenefitNotice'
import Checkbox from '~/app/components/common/Checkbox/YaComCheckbox'
import { getDomesticVipBadgeType } from '~/api/utils/memberClass'
import { getUseTimeText } from '~/api/converters/domestic/order/ProductSection/Property/utils'
import FetchRetryButton from '~/app/components/common/FetchRetry/FetchRetryButton'
import RatePlanBenefits from '~/app/components/common/RatePlanBenefits'

/**
 * DomesticAccommodationItem component
 * author: jaegeunlee
 * description
 */
const CheckDateWrapper = styled.div<{ disabled: boolean }>`
  color: ${({ disabled, theme }) => (disabled ? theme.color.gray400 : theme.color.gray900)};
`

const CheckTimeWrapper = styled.div<{ disabled: boolean }>`
  margin: 0.4rem 0;
  color: ${({ disabled, theme }) => (disabled ? theme.color.gray400 : theme.color.gray600)};
`

const CapacityWrapper = styled(CheckTimeWrapper)`
  margin: 0;
`

const ChangeRentTimeWrapper = styled.div`
  flex: 0 0 10rem;
  margin-left: 28px;
`

const OptionNameWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  .icon-wrapper {
    cursor: pointer;
    margin-left: 1.5rem;
    svg {
      path {
        fill: ${({ theme }) => theme.color.gray600};
      }
    }
  }

  .name-wrapper {
    width: 100%;
  }
`

const RatePlanBenefitsWrapper = styled.div<{ disabled: boolean }>`
  ${Typography('body2').regular};
  word-break: keep-all;
  margin: 0.6rem 0 0.8em;

  div {
    color: ${({ disabled, theme }) => (disabled ? theme.color.gray400 : theme.color.gray600)};
  }
`

const DomesticOptionDescriptionContainer = styled(OptionDescriptionContainer)`
  margin-bottom: 0.4rem;
`

const MemberClassBadge = styled(VipDiscountBadge)`
  margin-top: 0.4rem;
`

export type AccommodationOption = Extract<
  CartItemsV3Query['cartItemsV3'][number]['options'][number],
  { __typename?: 'DomesticAccommodationCartItemOption' }
>
export interface DomesticAccommodationItemProps {
  option: AccommodationOption
  currentAt: string
  category: PropertyCategory
  photo?: string
  onRentTimeClick?: (id: string) => void
  onCheck?: (checked: boolean, id: string) => void
  onClose?: (id: string) => void
  onTitleClick?: (id: string) => void
  onChangeRentTime?: () => void
  onCancelRefreshButtonClick?: () => void
}

const DomesticAccommodationItem: React.FC<DomesticAccommodationItemProps> = ({
  option,
  currentAt,
  category,
  photo,
  onRentTimeClick,
  onCheck,
  onClose,
  onTitleClick,
  onCancelRefreshButtonClick,
}) => {
  const [isCancelFree, setIsCancelFree] = useState(true)
  const remainCount: number | undefined = option.remains
  const propertyCoupons = option.propertyCoupons?.list || []
  const propertyCouponsMetas = option.propertyCouponsProgress?.[0]?.metas || []

  const isHotDeal = useMemo(() => {
    const { hotDeal } = option.ratePlan
    return hotDeal.isEnabled && hotDeal.discountRate > 0
  }, [option.ratePlan.hotDeal])

  const onChangeRentTimeClick = () => {
    onRentTimeClick?.(option.id)
  }

  const { checkinTime, checkinDayOfWeek } = useMemo(() => {
    const date = DateTime.fromFormat(option.checkInDate, 'yyyy-MM-dd')
    const time = DateTime.fromISO(option.checkInAt)
    return {
      checkinTime: time.toFormat('HH:mm'),
      checkinDayOfWeek: date.toFormat('ccc'),
    }
  }, [option.checkInAt])

  const { checkoutTime, checkoutDayOfWeek } = useMemo(() => {
    const date = DateTime.fromFormat(option.checkOutDate, 'yyyy-MM-dd')
    const time = DateTime.fromISO(option.checkOutAt)
    return {
      checkoutTime: time.toFormat('HH:mm'),
      checkoutDayOfWeek: date.toFormat('ccc'),
    }
  }, [option.checkOutAt])

  const isDayUse = useMemo(
    () => getHasDayUseBadge(option.roomType?.config?.badges),
    [option.roomType]
  )

  const useTimeText = useMemo(() => {
    if (isDayUse) {
      return ''
    }
    return getUseTimeText(
      option.checkInAt,
      option.checkOutAt,
      option.useType === DomesticAccommodationUseType.DayUse
    )
  }, [option, isDayUse])

  const useTypeText = useMemo(
    () =>
      isDayUse
        ? 'DayUse'
        : option.useType === DomesticAccommodationUseType.DayUse
          ? '대실'
          : option.useType === DomesticAccommodationUseType.OverNight
            ? '숙박'
            : '연박',
    [option.useType, isDayUse]
  )

  const statusText = useMemo(() => {
    if (!option.availability) {
      if (option.unavailableReason === 'SOLD_OUT' || option.unavailableReason === 'OUT_OF_STOCK') {
        if (option.useType === 'DAY_USE') {
          return '해당 시간 예약 마감'
        } else {
          return '예약 마감'
        }
      } else if (option.unavailableReason === 'STOP') {
        return '예약 마감'
      }
      return '예약불가'
    } else if (remainCount && remainCount <= 0) {
      return option.useType === DomesticAccommodationUseType.DayUse
        ? '해당시간 예약마감'
        : '예약마감'
    }
    return ''
  }, [option.availability, remainCount, option.outdated])

  const isDisabled = useMemo(
    () => !isAvailableDomesticAccommodationOption(option),
    [option.availability, option.remains, option.outdated]
  )

  const stockAlmostExhausted = useMemo(
    () =>
      !isDisabled &&
      remainCount &&
      remainCount < 5 &&
      category !== PropertyCategory.Pension &&
      category !== PropertyCategory.GuestHouse &&
      !option.ratePlan.isExceptionalCancelPolicy,
    [isDisabled, remainCount, category, option.ratePlan.isExceptionalCancelPolicy]
  )

  const couponBadgeText = useMemo(() => {
    if (propertyCoupons.length <= 0) {
      return ''
    }

    const progressCoupons = propertyCoupons.map(coupon => {
      const meta = propertyCouponsMetas.find(meta => coupon.couponIdx === meta.id)
      const progress =
        option.useType === DomesticAccommodationUseType.DayUse
          ? meta?.progress?.REST
          : option.useType === DomesticAccommodationUseType.OverNight
            ? meta?.progress?.SLEEP
            : meta?.progress?.DAYS
      const limitUse = progress ? progress.limitUse || 100 : 0
      const used = progress ? progress.used || 0 : 0
      return {
        ...coupon,
        limitUse,
        used,
        available: limitUse - used,
      }
    })
    const filteredProgressCoupons = progressCoupons.filter(coupon => {
      if (!coupon) {
        return false
      }
      /**
       * FES-656
       * usableRoomType : MYROOM 인 경우 , 무한쿠폰룸에서만 사용가능
       * usableRoomType : NORMAL인 경우, 무한쿠폰룸에서는 사용 불가능
       * usableRoomType : MYROOM, NORMAL인 경우 모두 사용 가능
       *
       * option.roomType.data.cloneType(roomType) 은 'MY_ROOM' 또는 null 로 응답함.
       *
       * YAWEB-1638
       * - 무한 쿠폰 룸 코드 클렌징
       */
      if (!coupon.usableRoomType.includes('NORMAL')) {
        return false
      }
      /**
       * 쿠폰 재고가 없다면 사용 불가
       */
      if (coupon.available <= 0) {
        return false
      }
      /**
       * YAWEB-2286
       * 쿠폰 최소 적용 가능 결제 금액 체크
       */
      if (option.price < (coupon?.applicableMinPrice ?? 0)) {
        return false
      }
      /**
       * 쿠폰에 사용가능한 useType 이 존재하지 않을 경우 사용 불가
       */
      if (option.useType === DomesticAccommodationUseType.DayUse) {
        if (coupon.product && coupon.product.indexOf('대실') < 0) {
          return false
        }
      } else if (option.useType === DomesticAccommodationUseType.OverNight) {
        if (coupon.product && coupon.product.indexOf('숙박') < 0) {
          return false
        }
      } else if (option.useType === DomesticAccommodationUseType.MultiNights) {
        if (coupon.product && coupon.product.indexOf('연박') < 0) {
          return false
        }
      }
      const current = DateTime.fromISO(currentAt)
      const week = ['월', '화', '수', '목', '금', '토', '일']
      const weekDay = week[current.weekday - 1]
      /**
       * 쿠폰에 사용가능한 요일이 존재 하지 않을 경우 사용 불가
       */
      return coupon.week.possibleWeek.indexOf(weekDay) >= 0
    })

    const coupons = getSortCartPropertyCoupons(filteredProgressCoupons)

    const coupon = coupons[0]
    if (!coupon) {
      return ''
    }
    const exhaustedText = coupon.available <= 2 ? ' 마감임박' : ''
    const intl = new Intl.NumberFormat('ko-KR', { style: 'decimal' })
    if (coupon) {
      if (coupon.priceType === 'PERCENT') {
        return `${coupon.price}%${exhaustedText}`
      }
      if (coupon.price >= 100000) {
        // 10 만원이 넘는 경우 천원 단위까지 절삭
        return `${Math.floor(coupon.price / 10000)}만원${exhaustedText}`
      } else if (coupon.price >= 10000) {
        // 만원이 넘는 경우 천원단위가 있다면 소수점으로 표기 120000원 -> 1.2만원
        // 만원이 넘는 경우 천원단위가 없다면 만원으로 표기 20000원 -> 2만원
        const price = Math.floor(coupon.price / 10000)
        const remainder = Math.floor((coupon.price % 10000) / 1000)
        return `${price}${remainder > 0 ? `.${remainder}` : ''}만원${exhaustedText}`
      } else if (coupon.price >= 1000) {
        // 천원이 넘는 경우 백원단위가 있다면 전체 표기 5500원 -> 5,500원
        // 천원이 없는 경우 백원단위가 없다면 천원으로 표기 5000원 -> 5천원
        const price = Math.floor(coupon.price / 1000)
        const remainder = Math.floor((coupon.price % 1000) / 100)
        if (remainder === 0) {
          return `${price}천원${exhaustedText}`
        }
        return `${intl.format(coupon.price)}원${exhaustedText}`
      } else {
        // 가격을 표기 3300원 -> 3,300원
        return `${intl.format(coupon.price)}원${exhaustedText}`
      }
    }

    return ''
  }, [propertyCoupons, currentAt])

  const toDotDate = useCallback(
    date => DateTime.fromFormat(date, 'yyyy-MM-dd').toFormat('yyyy.MM.dd'),
    [option]
  )

  const badges = useMemo(() => {
    return badgeWithPolicyInCartOption({
      optionBadges: option.roomType.config?.badges ?? [],
      ratePlanBadges: option.ratePlanConfig.badges ?? [],
      useType: option.useType,
    })
  }, [option])

  const cancelSummary = useMemo(
    (): CancelPolicySummary | undefined =>
      getDomesticAccommodationCancelSummary({ option, currentAt }),
    [option, currentAt]
  )

  const ratePlanBenefits = useMemo(() => {
    return getPreOrderRatePlanOptions({
      benefits: option.ratePlan.benefitList,
      config: option.ratePlanConfig,
    })
  }, [option.ratePlanConfig, option.ratePlan.benefitList])

  const vipDiscountPrice = useMemo(() => {
    const { originalPrice, price } = option
    return originalPrice ? originalPrice - price : 0
  }, [option])

  const isShowVipBenefitNotice = useMemo(() => {
    return (
      option.memberClass === 'LEVEL_01' &&
      vipDiscountPrice > 0 &&
      option.itemUpdatedByMemberClass &&
      !isDisabled
    )
  }, [option.memberClass, vipDiscountPrice, option.itemUpdatedByMemberClass, isDisabled])

  const memberClassBadgeType = useMemo(
    () => getDomesticVipBadgeType(option.memberClass),
    [option.memberClass]
  )

  return (
    <>
      <OptionNameWrapper>
        <div className="name-wrapper">
          <ConfigBadgeWrapper>
            <ConfigBadgeGroup badges={badges} />
          </ConfigBadgeWrapper>
          <OptionNameContainer disabled={isDisabled} onClick={() => onTitleClick?.(option.id)}>
            {option.roomTypeName}
          </OptionNameContainer>
        </div>
        <div className="icon-wrapper" onClick={() => onClose?.(option.id)}>
          <Icon_close size={1.6} unit="rem" />
        </div>
      </OptionNameWrapper>
      <ItemContainer>
        <Checkbox
          className="check-icon-wrapper"
          onClick={() => onCheck?.(isDisabled ? false : !option.isChosen, option.id)}
          checked={option.isChosen}
          disabled={isDisabled}
        />
        <div className="image-wrapper fix-height">
          <LazyLoadImage
            title={''}
            alt={''}
            height={80}
            src={photo}
            width={80}
            placeholderSrc={yaLogo}
          />
        </div>
        <DescContainer>
          {option.ratePlan.title && (
            <DomesticOptionDescriptionContainer disabled={isDisabled}>
              {option.ratePlan.title}
            </DomesticOptionDescriptionContainer>
          )}
          <CheckDateWrapper disabled={isDisabled}>
            <span>
              {toDotDate(option.checkInDate)} ({checkinDayOfWeek})
            </span>
            {option.useType !== DomesticAccommodationUseType.DayUse && !isDayUse && (
              <span>
                <span> ~ </span>
                <span>
                  {toDotDate(option.checkOutDate)} ({checkoutDayOfWeek})
                </span>
              </span>
            )}
            {!isDayUse && (
              <>
                <span className="bar">|</span>
                <span>{useTimeText}</span>
              </>
            )}
          </CheckDateWrapper>
          <CheckTimeWrapper disabled={isDisabled}>
            <span>체크인 {checkinTime}</span>
            <span className="bar">|</span>
            <span>체크아웃 {checkoutTime}</span>
          </CheckTimeWrapper>
          <CapacityWrapper disabled={isDisabled}>
            기준 {option.roomType.data.capacity}명 / 최대 {option.roomType.data.maxCapacity}명
          </CapacityWrapper>
          {!!ratePlanBenefits.length && (
            <RatePlanBenefitsWrapper disabled={isDisabled}>
              <RatePlanBenefits ratePlanBenefits={ratePlanBenefits} />
            </RatePlanBenefitsWrapper>
          )}
          {!isDisabled && !option.outdated && (
            <>
              <BadgeContainer>
                {isHotDeal && (
                  <div className="badge-wrapper">
                    <FillRectBadge>핫딜</FillRectBadge>
                  </div>
                )}
                {couponBadgeText && (
                  <div className="badge-wrapper">
                    <FillRectBadge>선착순쿠폰</FillRectBadge>
                  </div>
                )}
              </BadgeContainer>
            </>
          )}
          <MemberClassBadge type={memberClassBadgeType} />
          {option.outdated && (
            <WarningNotice>
              <Icon_info size={1.6} unit="rem" />
              <span>상품 정보가 변경되었습니다. 상품을 다시 선택해주세요.</span>
            </WarningNotice>
          )}
          {!isDisabled && !cancelSummary && <CancelPolicyFetchFailText />}
        </DescContainer>
      </ItemContainer>
      <PriceContainer disabled={isDisabled}>
        <ChangeRentTimeWrapper>
          {!isDisabled && option.useType === DomesticAccommodationUseType.DayUse && (
            <Button
              onClick={onChangeRentTimeClick}
              size={BUTTON_SIZE.XS}
              title={'시간 변경'}
              type={BUTTON_TYPE.BASIC}
              style={{ width: '80px' }}
            />
          )}
          {!cancelSummary && <FetchRetryButton onClick={onCancelRefreshButtonClick} />}
        </ChangeRentTimeWrapper>
        <div className="price-wrapper">
          <div>
            <span>{useTypeText}</span>
            <span className="price">{toKRW(option.price)}</span>
          </div>
          {cancelSummary && (
            <CancelPolicyText
              cancelPolicySummary={cancelSummary}
              setIsCancelFree={setIsCancelFree}
            />
          )}
          {stockAlmostExhausted && (
            <div className="almost-exhausted">남은객실 {option.remains}개</div>
          )}
          {statusText && <div className="status-text">{statusText}</div>}
          {!isDisabled && !option.outdated && (
            <ExceptionalCancelPolicyText
              isExceptionalCancelPolicy={option.ratePlan.isExceptionalCancelPolicy}
              isCancelFree={isCancelFree}
            />
          )}
        </div>
      </PriceContainer>

      {isShowVipBenefitNotice && <CartVipBenefitNotice discountPrice={vipDiscountPrice} />}
    </>
  )
}

export default DomesticAccommodationItem
